/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  assets: {
    all: ["assets"] as const,
    /** Live assets for the user. */
    list: (userId: string | undefined) => ["assets", "list", userId] as const,
    detail: (assetId: string | undefined) => ["assets", "detail", assetId] as const,
    /** Assets in the user's trash (soft-delete). */
    trashed: (userId: string | undefined) => ["assets", "trashed", userId] as const,
  },
  services: {
    all: ["services"] as const,
    /** Service history for an asset. */
    byAsset: (assetId: string | undefined) => ["services", "byAsset", assetId] as const,
    /** Annual costs (total + by category) for an asset. */
    annualCosts: (assetId: string | undefined) => ["services", "annualCosts", assetId] as const,
    /** All services for the user (analytics / reports). */
    allByUser: (userId: string | undefined) => ["services", "allByUser", userId] as const,
  },
  readings: {
    all: ["readings"] as const,
    /** Reading history for an asset. */
    byAsset: (assetId: string | undefined) => ["readings", "byAsset", assetId] as const,
    /** All readings for the user (expiry projection). */
    allByUser: (userId: string | undefined) => ["readings", "allByUser", userId] as const,
  },
  reminders: {
    all: ["reminders"] as const,
    /** Rules (with asset metadata) for the user. */
    rules: (userId: string | undefined) => ["reminders", "rules", userId] as const,
  },
  warranties: {
    all: ["warranties"] as const,
    /** Warranties (with asset metadata) for the user. */
    list: (userId: string | undefined) => ["warranties", "list", userId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
    byAsset: (assetId: string | undefined) => ["attachments", "byAsset", assetId] as const,
    byService: (serviceRecordId: string | undefined) =>
      ["attachments", "byService", serviceRecordId] as const,
  },
  search: {
    all: ["search"] as const,
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Upload-queue stats (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Is there local data? (profile cleanup button). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (getThemesQuery without pagination; profile + list-dialog SHARE the cache). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
    /**
     * Active sessions for the user. Hierarchical by design: `onRevokeSuccess`
     * invalidates the `activeSessions` PREFIX without knowing the tab's `sessionId`
     * (P23/CC-60 · P25).
     */
    activeSessions: ["auth", "activeSessions"] as const,
    activeSessionsFor: (currentSessionId: string | undefined) =>
      ["auth", "activeSessions", currentSessionId] as const,
  },
};
